<script setup lang="ts" generic="T extends unknown">
import { useScrollSliderCardControls } from "../composables/useScrollSliderCardControls";
import { SCROLL_DIRECTION } from "../model/types";
import IconButton from "~/components/button/IconButton.vue";
import { debounce } from "~/utils/debounce";

const props = withDefaults(defineProps<{
  items: T[],
  gap?: number,
}>(), {
  gap: 8,
});

const { gtag } = useGtag();

const {
  scrollContainer,
  carouselItems,
  isControlToPreviousEnabled,
  isControlToNextEnabled,
  handleScrollEnd,
  toStart,
  toNext,
  toPrevious,
} = useScrollSliderCardControls();

let scrollOffset = 0;

function scroll(direction: SCROLL_DIRECTION) {
  switch (direction) {
  case SCROLL_DIRECTION.toPrevious:
    toPrevious(scrollOffset);
    gtag("event", ScrollSliderEvents.prev);
    break;
  case SCROLL_DIRECTION.toNext:
    toNext(scrollOffset);
    gtag("event", ScrollSliderEvents.next);
    break;
  case SCROLL_DIRECTION.toStart:
    toStart();
    gtag("event", ScrollSliderEvents.start);
    break;
  }
}

const debouncedUpdateControls = debounce(handleScrollEnd, 100);

let resizeObserver: ResizeObserver | undefined;
let scrollAbortController: AbortController | undefined;
onMounted(() => {
  scrollAbortController = new AbortController();
  resizeObserver = new ResizeObserver((entries) => {
    scrollOffset = Math.round(entries[0].contentRect.width) + props.gap;
  });

  if (scrollContainer.value) {
    scrollContainer.value.addEventListener("scroll", debouncedUpdateControls, { signal: scrollAbortController.signal });
  }

  if (carouselItems.value) {
    resizeObserver.observe(carouselItems.value[0]);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  scrollAbortController?.abort();
});

</script>


<template>
  <div class="scroll-slider-card-wrapper">
    <div class="scroll-slider-card-container" ref="scrollContainer">
      <div class="scroll-slider-card__carousel" :style="{ gap: `${gap}px`}">
        <div
          class="scroll-slider-card__carousel--item"
          ref="carouselItems"
          v-for="item in items">
          <slot v-bind="{ item }" />
        </div>
      </div>
    </div>

    <div class="scroll-slider-control">
      <IconButton
        v-show="!isControlToPreviousEnabled"
        class="scroll-slider-control__start"
        icon="doubleArrow"
        height="32"
        width="32"
        @click="scroll(SCROLL_DIRECTION.toStart)"
      />
      <IconButton
        class="scroll-slider-control__prev"
        icon="arrow"
        height="32"
        width="32"
        :disabled="isControlToPreviousEnabled"
        @click="scroll(SCROLL_DIRECTION.toPrevious)"
      />
      <IconButton
        class="scroll-slider-control__next"
        icon="arrow"
        height="32"
        width="32"
        :disabled="isControlToNextEnabled"
        @click="scroll(SCROLL_DIRECTION.toNext)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "assets/styles/global/mixins" as m;
@use "assets/styles/global/colors" as c;
@use "assets/styles/global/variables" as v;

.scroll-slider-card-wrapper {
  display: flex;
  flex-direction: column;
}

.scroll-slider-card-container {
  scroll-padding: 0 16px;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.scroll-slider-card__carousel {
  display: flex;
  width: fit-content;
  padding: 0 16px;
  margin: 0 auto;
}

.scroll-slider-card__carousel--item {
  display: flex;
  scroll-snap-align: start;
}

@media screen and (min-width: v.$tablet) {
  .scroll-slider-card-container {
    scroll-padding: 0 v.$content-padding-tablet;
  }

  .scroll-slider-card__carousel {
    padding: 0 v.$content-padding-tablet;
  }
}

@media screen and (min-width: v.$desktop) {
  .scroll-slider-card-container {
    scroll-padding: 0 v.$content-padding-desktop;
  }

  .scroll-slider-card__carousel {
    padding: 0 v.$content-padding-desktop;
  }
}

@media screen and (min-width: v.$desktop-1440) {
  .scroll-slider-card-container {
    scroll-padding: 0 v.$content-padding-desktop-1440;
  }

  .scroll-slider-card__carousel {
    padding: 0 v.$content-padding-desktop-1440;
  }
}

@media screen and (max-width: v.$desktop), (hover: none) {
  .scroll-slider-card-container {
    overflow-x: auto;
  }
}
</style>